import { api } from "@/shared/api/base";
import { generateUuid, getSessionId } from "./analyticsSession";
const ENDPOINT_PATH = "/api/v1/public/analytics/events";
/** Страховочный интервал, если debounce не сработал. */
const FLUSH_INTERVAL_MS = 2000;
/** Основной путь: отправить очередь вскоре после события (пока вкладка жива). */
const FLUSH_DEBOUNCE_MS = 250;
const BATCH_LIMIT = 100;
const MAX_QUEUE = 500;
let queue = [];
let flushTimer = null;
let debounceTimer = null;
let listenersAttached = false;
/** Сериализуем flush, чтобы hide не гонялся с debounce и не терял пачку. */
let flushChain = Promise.resolve();
function endpointUrl() {
    const base = api.defaults.baseURL || "";
    return `${base}${ENDPOINT_PATH}`;
}
function cleanProperties(properties) {
    if (!properties) {
        return undefined;
    }
    const out = {};
    for (const [k, v] of Object.entries(properties)) {
        if (v !== undefined && v !== null) {
            out[k] = v;
        }
    }
    return Object.keys(out).length ? out : undefined;
}
function scheduleFlush() {
    ensureStarted();
    if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void flush();
    }, FLUSH_DEBOUNCE_MS);
}
function flushOnHide() {
    if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
    void flush().finally(() => {
        flushWithBeacon();
    });
}
function ensureStarted() {
    if (typeof window === "undefined") {
        return;
    }
    if (!flushTimer) {
        flushTimer = setInterval(() => {
            void flush();
        }, FLUSH_INTERVAL_MS);
    }
    if (!listenersAttached) {
        listenersAttached = true;
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                flushOnHide();
            }
        });
        window.addEventListener("pagehide", flushOnHide);
        window.addEventListener("beforeunload", flushOnHide);
    }
}
/**
 * Регистрирует событие. type — только из AnalyticsEvent; properties — плоский
 * snake_case объект (числа числами, без PII: длины вместо текстов).
 */
export function trackEvent(type, properties) {
    if (typeof window === "undefined") {
        return;
    }
    const event = {
        id: generateUuid(),
        type,
        ts: new Date().toISOString(),
        session_id: getSessionId(),
    };
    const props = cleanProperties(properties);
    if (props) {
        event.properties = props;
    }
    queue.push(event);
    if (queue.length > MAX_QUEUE) {
        queue = queue.slice(-MAX_QUEUE);
    }
    scheduleFlush();
}
async function flushOnce() {
    if (!queue.length) {
        return;
    }
    const batch = queue.slice(0, BATCH_LIMIT);
    queue = queue.slice(BATCH_LIMIT);
    try {
        await api.post(ENDPOINT_PATH, {
            session_id: getSessionId(),
            events: batch,
        });
        if (queue.length) {
            await flushOnce();
        }
    }
    catch {
        queue = batch.concat(queue).slice(-MAX_QUEUE);
    }
}
/** Отправка очереди через axios (обычный путь: куки + X-Session-Id заголовок). */
export function flush() {
    flushChain = flushChain.then(flushOnce, flushOnce);
    return flushChain;
}
/**
 * Доставка остатка очереди при закрытии вкладки. sendBeacon не умеет
 * кастомные заголовки; session_id в теле. Cross-origin куки могут не уйти —
 * основной путь при hide: axios flush выше.
 */
function flushWithBeacon() {
    if (!queue.length) {
        return;
    }
    const batch = queue.slice(0, BATCH_LIMIT);
    queue = queue.slice(BATCH_LIMIT);
    const body = JSON.stringify({ session_id: getSessionId(), events: batch });
    try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            const blob = new Blob([body], { type: "application/json" });
            const ok = navigator.sendBeacon(endpointUrl(), blob);
            if (ok) {
                return;
            }
        }
    }
    catch {
        /* падаем в fetch ниже */
    }
    try {
        void fetch(endpointUrl(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Session-Id": getSessionId(),
            },
            body,
            credentials: "include",
            keepalive: true,
        });
    }
    catch {
        /* потеряли пачку при закрытии — допустимо */
    }
}
